import React, { useState, useEffect } from 'react'
import { useCare } from '../../hooks/useCare'
import { useAuth } from '../../hooks/useAuth'
import { CaseMember, UserRole, getRoleInfo, getDefaultPermissions } from '../../types/user'
import Avatar from '../common/Avatar'
import AvatarEditor from '../common/AvatarEditor'
import PetAvatarModalEditor from '../auth/PetAvatarModalEditor'
import CreateUserModal from './CreateUserModal'
import ShareInviteModal from './ShareInviteModal'
import { doc, getDoc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore'
import { db } from '../../config/firebase'
import { generateCaregiverInviteCode } from '../../utils/inviteCodeGenerator'
import { useToast } from '../../contexts/ToastContext'
import { useNavigate } from 'react-router-dom'

// Componente para eliminar caso
const DeleteCaseModal: React.FC<{
  currentCase: any
  onClose: () => void
  onConfirm: () => Promise<void>
}> = ({ currentCase, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const confirmWord = 'eliminar'

  const handleConfirmDelete = async () => {
    if (confirmText.toLowerCase() !== confirmWord) return

    setLoading(true)
    try {
      await onConfirm()
    } finally {
      setLoading(false)
    }
  }

  const isConfirmValid = confirmText.toLowerCase() === confirmWord

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
            ⚠️ Eliminar Caso
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Estás a punto de eliminar permanentemente el caso <strong>"{currentCase?.name}"</strong>.
          </p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <p className="text-red-800 dark:text-red-200 text-sm">
              <strong>⚠️ Esta acción es irreversible</strong><br />
              Se eliminarán:
            </p>
            <ul className="text-red-700 dark:text-red-300 text-sm mt-2 ml-4 list-disc">
              <li>Todos los medicamentos del caso</li>
              <li>Historial de actividades</li>
              <li>Todos los miembros del caso</li>
              <li>Invitaciones pendientes</li>
            </ul>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Para confirmar, escribe <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{confirmWord}</span>:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
              placeholder={`Escribe "${confirmWord}" para confirmar`}
              disabled={loading}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmDelete}
            disabled={!isConfirmValid || loading}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              '🗑️'
            )}
            {loading ? 'Eliminando...' : 'Eliminar Caso'}
          </button>
        </div>
      </div>
    </div>
  )
}

const UserManagement: React.FC = () => {
  const { user } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const {
    currentCase,
    caseMembers,
    currentMembership,
    permissions,
    isAdmin,
    loadCaseMembers,
    inviteUser,
    updateMemberRole,
    removeMember,
    updateCase,
    deleteCase,
    loadUserCases
  } = useCare()

  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showProfileConfigModal, setShowProfileConfigModal] = useState(false)
  const [showUserConfigModal, setShowUserConfigModal] = useState(false)
  const [showEditProfileModal, setShowEditProfileModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState<CaseMember | null>(null)
  const [loading, setLoading] = useState(false)
  const [promotingToAdmin, setPromotingToAdmin] = useState(false)
  const [memberProfiles, setMemberProfiles] = useState<{[userId: string]: any}>({})
  const [showInviteInfoModal, setShowInviteInfoModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareModalType, setShareModalType] = useState<'caregiver' | 'patient'>('caregiver')
  const [showInvitationsSection, setShowInvitationsSection] = useState(false)
  const [showUsersSection, setShowUsersSection] = useState(true)
  const [showDeleteCaseModal, setShowDeleteCaseModal] = useState(false)
  const [showAvatarConfigModal, setShowAvatarConfigModal] = useState(false)
  const [avatarLoading, setAvatarLoading] = useState(false)

  // Verificar si el perfil de cuidado ya fue reclamado
  const isProfileClaimed = currentCase?.profileUserId != null
  
  // Buscar si el perfil de cuidado está en los miembros
  const profileMember = caseMembers.find(member => member.userId === currentCase?.profileUserId)

  // Verificar si el perfil de cuidado está en los miembros
  const profileMemberInTeam = caseMembers.find(member => 
    member.userId === currentCase?.profileUserId && member.role === 'care_recipient'
  )

  // Verificar si ya hay un asistido (care_recipient) en el caso
  const hasAssistedMember = caseMembers.some(member => member.role === 'care_recipient')

  // Verificar si el usuario actual es el creador del caso pero no es admin
  const isCreatorButNotAdmin = currentCase?.adminId === user?.uid && !isAdmin
  
  // Verificar si el usuario actual está en el equipo
  const currentUserMembership = caseMembers.find(member => member.userId === user?.uid)

  // Función para promocionar al creador a administrador
  const promoteCreatorToAdmin = async () => {
    if (!currentCase || !user?.uid || !isCreatorButNotAdmin) return
    
    setPromotingToAdmin(true)
    try {
      // Si el usuario ya tiene membresía, actualizarla
      if (currentUserMembership) {
        await updateDoc(doc(db, 'case-members', currentUserMembership.id), {
          role: 'master_admin',
          permissions: getDefaultPermissions('master_admin'),
          nickname: 'Creador (Admin)',
          updatedAt: serverTimestamp()
        })
        
        console.log('✅ Usuario promocionado a master_admin')
      } else {
        // Si no tiene membresía, crearla
        const adminMembership = {
          userId: user.uid,
          caseId: currentCase.id,
          role: 'master_admin' as UserRole,
          permissions: getDefaultPermissions('master_admin'),
          joinedAt: serverTimestamp(),
          invitedBy: user.uid,
          isActive: true,
          nickname: 'Creador (Admin)',
          notes: 'Promocionado automáticamente como creador del caso'
        }
        
        await addDoc(collection(db, 'case-members'), adminMembership)
        console.log('✅ Membresía de administrador creada')
      }
      
      // Recargar miembros del caso
      await loadCaseMembers(currentCase.id)
      
      toast.success(
        '¡Promoción exitosa!',
        'Ahora eres administrador del caso y puedes gestionar otros usuarios.'
      )
      
    } catch (error) {
      console.error('Error promoting creator to admin:', error)
      toast.error(
        'Error al promocionar',
        'No se pudo promocionar a administrador. Inténtalo de nuevo.'
      )
    } finally {
      setPromotingToAdmin(false)
    }
  }

  // Función automática para detectar y promocionar al creador
  const autoPromoteCreatorIfNeeded = async () => {
    if (!currentCase || !user?.uid || isAdmin) return
    
    // Solo promover automáticamente si es el creador y no hay otros administradores
    const hasAdmins = caseMembers.some(member => 
      member.role === 'master_admin' || member.role === 'admin'
    )
    
    if (currentCase.adminId === user.uid && !hasAdmins) {
      console.log('🔄 Detectado creador sin permisos de admin, promocionando automáticamente...')
      await promoteCreatorToAdmin()
    }
  }

  // Ejecutar auto-promoción cuando se cargan los miembros
  useEffect(() => {
    if (currentCase && caseMembers.length > 0) {
      autoPromoteCreatorIfNeeded()
    }
  }, [currentCase, caseMembers, user?.uid])

  // Generar código de invitación para el perfil de cuidado
  const generateProfileInviteCode = async () => {
    if (!currentCase || !permissions.canManageUsers) return
    
    setLoading(true)
    try {
      // Generar código único
      const profileInviteCode = Math.random().toString(36).substring(2, 15).toUpperCase()
      
      // Actualizar el caso con el nuevo código
      await updateCase(currentCase.id, { profileInviteCode })
      toast.success(
        '¡Código generado!',
        `Código de invitación para asistido: ${profileInviteCode}`
      )
      
    } catch (error) {
      console.error('Error generating profile invite code:', error)
      toast.error(
        'Error al generar código',
        'No se pudo generar el código de invitación. Inténtalo de nuevo.'
      )
    } finally {
      setLoading(false)
    }
  }

  // Generar código de invitación para cuidadores
  const handleGenerateCaregiverInviteCode = async () => {
    if (!currentCase || !permissions.canManageUsers) return
    
    setLoading(true)
    try {
      // Generar código único con formato amigable
      const inviteCode = generateCaregiverInviteCode()
      
      // Actualizar el caso con el nuevo código
      await updateCase(currentCase.id, { inviteCode })
      toast.success(
        '¡Código generado!',
        `Código de invitación para cuidadores: ${inviteCode}`
      )
      
    } catch (error) {
      console.error('Error generating caregiver invite code:', error)
      toast.error(
        'Error al generar código',
        'No se pudo generar el código de invitación. Inténtalo de nuevo.'
      )
    } finally {
      setLoading(false)
    }
  }

  // Funciones para copiar códigos y enlaces
  const copyProfileInviteUrl = () => {
    if (!currentCase?.profileInviteCode) return
    navigator.clipboard.writeText(`${window.location.origin}/?invite=${currentCase.profileInviteCode}&type=assisted`)
    toast.success('¡Copiado!', 'Enlace copiado al portapapeles')
  }

  const copyCaregiverInviteUrl = () => {
    if (!currentCase?.inviteCode) return
    navigator.clipboard.writeText(`${window.location.origin}/?invite=${currentCase.inviteCode}&type=caregiver`)
    toast.success('¡Copiado!', 'Enlace copiado al portapapeles')
  }

  // Abrir modal de compartir para cuidadores
  const openShareModal = (type: 'caregiver' | 'patient') => {
    setShareModalType(type)
    setShowShareModal(true)
  }

  // Cargar miembros al montar el componente
  useEffect(() => {
    if (currentCase?.id) {
      loadCaseMembers(currentCase.id)
    }
  }, [currentCase?.id, loadCaseMembers])

  // Cargar perfiles de miembros para obtener información de avatares
  const loadMemberProfiles = async () => {
    if (!caseMembers.length) return
    
    try {
      const profiles: {[userId: string]: any} = {}
      
      for (const member of caseMembers) {
        const userDoc = await getDoc(doc(db, 'users', member.userId))
        if (userDoc.exists()) {
          const userData = userDoc.data()
          profiles[member.userId] = {
            avatarSeed: userData.avatarSeed,
            avatarCustomization: userData.avatarCustomization,
            name: userData.name || userData.username
          }
        }
      }
      
      setMemberProfiles(profiles)
    } catch (error) {
      console.error('Error loading member profiles:', error)
    }
  }

  // Cargar perfiles cuando cambian los miembros
  useEffect(() => {
    loadMemberProfiles()
  }, [caseMembers])

  const handleCreateUserSuccess = () => {
    // El modal se encarga de recargar los miembros
    setShowCreateUserModal(false)
  }

  const handleConfigureUser = (member: CaseMember) => {
    setSelectedMember(member)
    setShowUserConfigModal(true)
  }

  const handleEditProfile = (member: CaseMember) => {
    setSelectedMember(member)
    setShowEditProfileModal(true)
  }

  const handleRoleChange = async (memberId: string, newRole: UserRole) => {
    if (!isAdmin) return

    try {
      await updateMemberRole(memberId, newRole)
      await loadCaseMembers(currentCase!.id)
    } catch (error) {
      console.error('Error updating role:', error)
      alert('Error al actualizar el rol')
    }
  }

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!isAdmin) return
    
    // Verificar si es la persona bajo cuidado
    const memberToRemove = caseMembers.find(m => m.id === memberId)
    const isCareRecipientMember = memberToRemove?.role === 'care_recipient'
    
    const confirmMessage = isCareRecipientMember 
      ? `⚠️ ATENCIÓN: Estás a punto de remover a "${memberName}" que es la PERSONA BAJO CUIDADO.\n\n` +
        `Esto significa que:\n` +
        `• Se perderá el acceso de ${memberName} a su información médica\n` +
        `• Podrás invitar a ${memberName} nuevamente más tarde si es necesario\n` +
        `• Todos los datos médicos se mantendrán en el caso\n\n` +
        `¿Estás seguro de que quieres continuar?`
      : `¿Estás seguro de que quieres remover a "${memberName}" del equipo de cuidado?\n\nEsta acción no se puede deshacer. El usuario perderá acceso a este caso y a todos sus datos asociados.`
    
    if (confirm(confirmMessage)) {
      try {
        await removeMember(memberId)
        await loadCaseMembers(currentCase!.id)
        
        const successMessage = isCareRecipientMember
          ? `${memberName} (persona bajo cuidado) ha sido removido del caso`
          : `${memberName} ha sido removido exitosamente del equipo`
        
        toast.success('Miembro removido', successMessage)
      } catch (error) {
        console.error('Error removing member:', error)
        toast.error(
          'Error al remover',
          'No se pudo remover el miembro. Inténtalo de nuevo.'
        )
      }
    }
  }

  const MemberCard: React.FC<{ member: CaseMember }> = ({ member }) => {
    const roleInfo = getRoleInfo(member.role)
    const isCurrentUser = member.userId === user?.uid
    const isCareRecipient = member.role === 'care_recipient'
    const memberPermissions = getDefaultPermissions(member.role)
    const currentUserIsAdmin = permissions.canManageUsers
    
    // Obtener información personalizada del avatar
    const memberProfile = memberProfiles[member.userId]
    const avatarSeed = memberProfile?.avatarSeed || member.userId
    const avatarCustomization = memberProfile?.avatarCustomization

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleEditProfile(member)}
              className="hover:opacity-80 transition-opacity"
              title="Editar avatar"
            >
              <Avatar
                user={{
                  type: 'human',
                  name: member.nickname || 'Usuario',
                  userId: member.userId, // ID único del miembro
                  avatarCustomization: memberProfiles[member.userId]?.avatarCustomization // Configuración personalizada
                }}
                size="md"
              />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {member.nickname || 'Usuario'}
                </h3>
                {isCurrentUser && currentUserIsAdmin && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                    Tú (Admin)
                  </span>
                )}
                {isCurrentUser && !currentUserIsAdmin && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                    Tú
                  </span>
                )}
                {member.userId === currentCase?.adminId && (
                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
                    👑 Creador
                  </span>
                )}
                {isCareRecipient && (
                  <span className="px-2 py-1 text-xs bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 rounded-full">
                    Bajo Cuidado
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Miembro desde {member.joinedAt.toLocaleDateString()}
              </p>
            </div>
          </div>

          {(currentUserIsAdmin || isCurrentUser) && (
            <button
              onClick={() => handleConfigureUser(member)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1"
              title="Configurar usuario"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          )}
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {roleInfo.description}
        </p>

        {/* Permisos resumidos */}
        <div className="flex flex-wrap gap-1 mb-3">
          {memberPermissions.canMarkAsTaken && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
              Marcar tomado
            </span>
          )}
          {memberPermissions.canAdjustStock && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
              Ajustar stock
            </span>
          )}
          {memberPermissions.canEditMedications && (
            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
              Editar medicamentos
            </span>
          )}
          {memberPermissions.canManageUsers && (
            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded">
              Administrar usuarios
            </span>
          )}
        </div>




      </div>
    )
  }

  if (!currentCase) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 dark:text-gray-400">
          No hay caso seleccionado
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Alerta para creador sin permisos de admin */}
      {isCreatorButNotAdmin && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                👑 Eres el creador de este caso pero no tienes permisos de administrador
              </h3>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                Como creador del caso, deberías tener permisos de administrador para gestionar el equipo y configuraciones.
              </p>
              <div className="mt-3">
                <button
                  onClick={promoteCreatorToAdmin}
                  disabled={promotingToAdmin}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  {promotingToAdmin ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Promocionando...
                    </>
                  ) : (
                    <>
                      👑 Hacerme Administrador
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Título del Caso Actual */}
      <div className="mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            📋 Caso Actual
          </h2>
          <h3 className="text-lg text-gray-700 dark:text-gray-300 mt-1">
            {currentCase.name}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 font-mono">
            ID: {currentCase.id}
          </p>
        </div>
      </div>

      {/* Nueva Sección de Configuración del Caso */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Configuración del Caso
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Personalizar y gestionar el caso actual
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Configurar Avatar */}
          {permissions.canChangeSettings && (
            <button
              onClick={() => {
                setShowAvatarConfigModal(true)
              }}
              className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Configurar Avatar
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Personalizar el avatar del caso
                </p>
              </div>
            </button>
          )}

          {/* Eliminar Caso */}
          {permissions.canManageUsers && (
            <button
              onClick={() => setShowDeleteCaseModal(true)}
              className="flex items-center gap-3 p-4 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Eliminar Caso
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Eliminar permanentemente el caso
                </p>
              </div>
            </button>
          )}
        </div>

        {/* Mostrar mensaje si no tiene permisos */}
        {!permissions.canChangeSettings && !permissions.canManageUsers && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-sm">No tienes permisos para configurar este caso</p>
          </div>
        )}
      </div>

      {/* Sección de Invitaciones */}
      {permissions.canManageUsers && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <button
            onClick={() => setShowInvitationsSection(!showInvitationsSection)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Invitaciones
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Gestionar invitaciones para el caso
                </p>
              </div>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showInvitationsSection ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showInvitationsSection && (
            <div className="mt-6 space-y-4">
              {/* Invitación para Cuidadores */}
              <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-900/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-green-800 dark:text-green-200">
                      👨‍⚕️ Invitar Cuidadores
                    </h3>
                    <p className="text-xs text-green-600 dark:text-green-300">
                      Para familiares, enfermeros, doctores, etc.
                    </p>
                  </div>
                  <button
                    onClick={() => openShareModal('caregiver')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    Compartir Invitación
                  </button>
                </div>
              </div>

              {/* Invitación para Asistido - Solo si no hay asistido */}
              {!hasAssistedMember && (
                <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                        🙋‍♂️ Invitar Asistido Nuevo
                      </h3>
                      <p className="text-xs text-blue-600 dark:text-blue-300">
                        Persona bajo cuidado - Solo puede usarse una vez
                      </p>
                    </div>
                    <button
                      onClick={() => openShareModal('patient')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                      </svg>
                      Compartir Invitación
                    </button>
                  </div>
                </div>
              )}

              {/* Mensaje cuando ya hay asistido */}
              {hasAssistedMember && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                      ✅ Persona Asistida Ya Registrada
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Ya hay una persona bajo cuidado en este caso. Solo puede haber una persona asistida por caso.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sección de Usuarios */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <button
          onClick={() => setShowUsersSection(!showUsersSection)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 01 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 01 9.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                Usuarios del Caso
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowInviteInfoModal(true)
                  }}
                  className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                  title="¿Cómo funcionan las invitaciones?"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {caseMembers.length} {caseMembers.length === 1 ? 'miembro' : 'miembros'}
              </p>
            </div>
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showUsersSection ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showUsersSection && (
          <div className="mt-6">
            {caseMembers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseMembers.map((member) => (
                  <MemberCard key={member.id} member={member} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 01 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 01 9.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  No hay usuarios en este caso
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Crear Usuario */}
      <CreateUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onSuccess={handleCreateUserSuccess}
      />

      {/* Modal de Configuración del Perfil */}
      {showProfileConfigModal && (
        <AvatarEditor
          isOpen={showProfileConfigModal}
          onClose={() => setShowProfileConfigModal(false)}
          onSave={async (customization, seed) => {
            if (!currentCase || !permissions.canManageUsers) return
            
            setLoading(true)
            try {
              await updateCase(currentCase.id, {
                profileInfo: {
                  ...currentCase.profileInfo,
                  avatarSeed: seed,
                  avatarStyle: 'avataaars',
                  avatarCustomization: customization
                }
              })
              
              setShowProfileConfigModal(false)
              alert('Avatar del perfil actualizado exitosamente')
              
            } catch (error) {
              console.error('Error updating profile avatar:', error)
              alert('Error al actualizar el avatar del perfil')
            } finally {
              setLoading(false)
            }
          }}
          initialSeed={currentCase?.profileInfo?.avatarSeed || currentCase?.profileInfo?.name || 'default'}
          initialCustomization={currentCase?.profileInfo?.avatarCustomization || {}}
          title={`Personalizar Avatar de ${currentCase?.profileInfo?.name}`}
          userName={currentCase?.profileInfo?.name || 'Perfil'}
        />
      )}

      {/* Modal de Información sobre Invitaciones */}
      {showInviteInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  ¿Cómo funcionan las invitaciones?
                </h3>
                <button
                  onClick={() => setShowInviteInfoModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-2 flex items-center gap-2">
                    👨‍⚕️ Invitación para Cuidadores
                  </h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• Permite a familiares, enfermeros, etc. unirse como cuidadores</li>
                    <li>• Pueden gestionar medicamentos, ver historial y configurar recordatorios</li>
                  </ul>
                </div>
                
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-2 flex items-center gap-2">
                    🙋‍♂️ Invitación para Persona Asistida
                  </h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <li>• Solo para la persona que recibe el cuidado ({currentCase?.profileInfo?.name})</li>
                    <li>• Puede ver su historial personal y marcar medicamentos como tomados</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 <strong>Tip:</strong> Los enlaces directos son más fáciles de usar. Al hacer clic, se abre la aplicación con el código pre-cargado.
                  </p>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowInviteInfoModal(false)}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showShareModal && currentCase && (
        <ShareInviteModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          inviteCode={shareModalType === 'caregiver' ? currentCase.inviteCode || '' : currentCase.profileInviteCode || ''}
          inviteUrl={shareModalType === 'caregiver' 
            ? `${window.location.origin}/?invite=${currentCase.inviteCode}&type=caregiver`
            : `${window.location.origin}/?invite=${currentCase.profileInviteCode}&type=assisted`
          }
          caseId={currentCase.id}
          caseInfo={{
            name: currentCase.name,
            profileName: 'Asistido',
            profileType: currentCase.profileType,
            avatar: currentCase.profileInfo?.customAvatar,
            avatarCustomization: currentCase.profileInfo?.avatarCustomization,
            petAvatarConfig: currentCase.profileInfo?.petAvatarConfig
          }}
          inviteType={shareModalType}
        />
      )}

      {/* Modal de Editar Perfil */}
      {showEditProfileModal && selectedMember && (
        <AvatarEditor
          isOpen={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          onSave={async (customization, seed) => {
            if (!selectedMember) return
            
            setLoading(true)
            try {
              // Actualizar el perfil del usuario
              await updateDoc(doc(db, 'users', selectedMember.userId), {
                avatarSeed: seed,
                avatarCustomization: customization,
                updatedAt: serverTimestamp()
              })
              
              setShowEditProfileModal(false)
              setSelectedMember(null)
              toast.success('¡Avatar actualizado!', 'El avatar del usuario ha sido actualizado exitosamente')
              
              // Recargar perfiles de miembros
              await loadMemberProfiles()
              
            } catch (error) {
              console.error('Error updating user avatar:', error)
              toast.error('Error', 'No se pudo actualizar el avatar. Inténtalo de nuevo.')
            } finally {
              setLoading(false)
            }
          }}
          initialSeed={memberProfiles[selectedMember.userId]?.avatarSeed || selectedMember.userId}
          initialCustomization={memberProfiles[selectedMember.userId]?.avatarCustomization || {}}
          title={`Personalizar Avatar de ${selectedMember.nickname || 'Usuario'}`}
          userName={selectedMember.nickname || 'Usuario'}
        />
      )}

      {/* Modal de Configurar Usuario */}
      {showUserConfigModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Configurar Usuario
              </h3>
              <button
                onClick={() => setShowUserConfigModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Avatar
                  user={{
                    type: 'human',
                    name: selectedMember.nickname || 'Usuario',
                    userId: selectedMember.userId, // ID único del miembro seleccionado
                    avatarCustomization: memberProfiles[selectedMember.userId]?.avatarCustomization // Configuración personalizada
                  }}
                  size="md"
                />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {selectedMember.nickname || 'Usuario'}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {getRoleInfo(selectedMember.role).name}
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  Información del Usuario
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">ID de usuario:</span>
                    <span className="text-gray-900 dark:text-white font-mono text-xs">{selectedMember.userId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Rol:</span>
                    <span className="text-gray-900 dark:text-white">{getRoleInfo(selectedMember.role).name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Miembro desde:</span>
                    <span className="text-gray-900 dark:text-white">{selectedMember.joinedAt.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 <strong>Tip:</strong> Usa el botón de editar perfil para cambiar el avatar del usuario.
                </p>
              </div>

              {/* Botón de Eliminar Usuario */}
              {currentMembership && permissions.canManageUsers && selectedMember.userId !== user?.uid && (
                <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/10">
                  <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                    ⚠️ Zona de Peligro
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                    Esta acción eliminará permanentemente a este usuario del caso actual.
                  </p>
                  <button
                    onClick={() => {
                      setShowUserConfigModal(false)
                      handleRemoveMember(selectedMember.id, selectedMember.nickname || 'Usuario')
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Eliminar Usuario del Caso
                  </button>
                </div>
              )}

            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowUserConfigModal(false)}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Eliminar Caso */}
      {showDeleteCaseModal && (
        <DeleteCaseModal
          currentCase={currentCase}
          onClose={() => setShowDeleteCaseModal(false)}
          onConfirm={async () => {
            try {
              await deleteCase(currentCase.id)
              await loadUserCases()
              toast.success('¡Éxito!', 'Caso eliminado exitosamente')
              setShowDeleteCaseModal(false)
              
              // Si eliminamos el caso actual, limpiar localStorage
              if (localStorage.getItem('currentCaseId') === currentCase.id) {
                localStorage.removeItem('currentCaseId')
              }
              
              // Navegar al home
              navigate('/')
            } catch (error) {
              console.error('Error deleting case:', error)
              toast.error('Error', 'No se pudo eliminar el caso. Inténtalo de nuevo.')
            }
          }}
        />
      )}

             {/* Modal de Configuración de Avatar del Caso */}
       {showAvatarConfigModal && currentCase?.profileType === 'pet' ? (
         <PetAvatarModalEditor
           isOpen={showAvatarConfigModal}
           onClose={() => setShowAvatarConfigModal(false)}
           onSave={async (petConfig, seed) => {
             if (!currentCase || !permissions.canChangeSettings) return
             
             setAvatarLoading(true)
             try {
               await updateCase(currentCase.id, {
                 profileInfo: {
                   ...currentCase.profileInfo,
                   avatarSeed: seed,
                   avatarStyle: 'pet',
                   petAvatarConfig: petConfig
                 }
               })
               toast.success('¡Avatar actualizado!', 'El avatar de la mascota ha sido actualizado exitosamente')
             } catch (error) {
               console.error('Error updating pet avatar:', error)
               toast.error('Error', 'No se pudo actualizar el avatar de la mascota. Inténtalo de nuevo.')
             } finally {
               setAvatarLoading(false)
             }
           }}
           initialSeed={currentCase?.profileInfo?.avatarSeed || currentCase?.profileInfo?.name || 'pet'}
           initialConfig={currentCase?.profileInfo?.petAvatarConfig}
           title={`Personalizar Avatar de ${currentCase?.profileInfo?.name || 'Mascota'}`}
           petName={currentCase?.profileInfo?.name || 'Mascota'}
         />
       ) : showAvatarConfigModal && currentCase?.profileType === 'human' ? (
         <AvatarEditor
           isOpen={showAvatarConfigModal}
           onClose={() => setShowAvatarConfigModal(false)}
           onSave={async (customization, seed) => {
             if (!currentCase || !permissions.canChangeSettings) return
             
             setAvatarLoading(true)
             try {
               await updateCase(currentCase.id, {
                 profileInfo: {
                   ...currentCase.profileInfo,
                   avatarSeed: seed,
                   avatarStyle: 'avataaars',
                   avatarCustomization: customization
                 }
               })
               toast.success('¡Avatar actualizado!', 'El avatar del caso ha sido actualizado exitosamente')
             } catch (error) {
               console.error('Error updating case avatar:', error)
               toast.error('Error', 'No se pudo actualizar el avatar del caso. Inténtalo de nuevo.')
             } finally {
               setAvatarLoading(false)
             }
           }}
           initialSeed={currentCase?.profileInfo?.avatarSeed || currentCase?.profileInfo?.name || 'default'}
           initialCustomization={currentCase?.profileInfo?.avatarCustomization || {}}
           title={`Personalizar Avatar de ${currentCase?.profileInfo?.name || 'Usuario'}`}
           userName={currentCase?.profileInfo?.name || currentCase?.name || 'Usuario'}
         />
       ) : null}
    </div>
  )
}

// Modales simplificados para que compile
const InviteUserModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}> = ({ isOpen, onClose, onSuccess }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-4">Invitar Usuario</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Funcionalidad de invitación por implementar
        </p>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserManagement 